import { Box, Flex, Group, Skeleton, Stack } from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  HammerPriceCardEmptyState,
  PerformanceBars,
  SortActionIcon,
  TimeframeInput,
} from "@/components/elements";
import { ContentLocker } from "@/components/widgets/ContentLocker";
import { defaultCurrency } from "@/lib/currency";
import { formatCurrency } from "@/utils/formatEuro";

import type { SortActionDirection, TimeframeInputProps } from "@/components/elements";
import type { AuctionsHammerPrices } from "@/types/auctions";
import type { Currency } from "@/types/currency";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type HammerPriceCardProps = {
  data?: AuctionsHammerPrices;
  defaultDirection?: SortActionDirection;
  defaultTimeframe?: TimeframeInputProps["defaultValue"];
  onTimeframeChange?: TimeframeInputProps["onChange"];
  limit?: number;
  loading?: boolean;
  enableContentLocker?: boolean;
  emptyStateTitle: string;
  emptyStateVariant: "conditions" | "year";
} & MantineStyleProps;

export const HammerPriceCard: FC<HammerPriceCardProps> = ({
  defaultDirection = "best",
  defaultTimeframe,
  data,
  limit = 5,
  loading = false,
  enableContentLocker = true,
  emptyStateTitle,
  emptyStateVariant,
  onTimeframeChange,
  ...props
}) => {
  const { t } = useTranslation();
  const [sortDirection, setSortDirection] = useState(defaultDirection);
  const [showPlanModal, { open: openPlanModal, close: closePlanModal }] = useDisclosure();

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const prices =
    data?.[sortDirection]
      .filter((item) => item.label.toLowerCase() !== "project")
      .slice(0, limit)
      .map((item, idx) => ({
        index: idx + 1,
        label: t(`components.HammerPriceCard.label.${item.label}`, item.label),
        value: item.value,
      })) || [];

  /*   if (!data) return null; */ // Uncomment this line when BE is ready to not return empty data

  const hasData = (data && data?.best.length > 0) || (data && data?.worst.length > 0);

  return (
    <>
      <Stack {...props}>
        <Group align="center" justify="space-between">
          <Box>
            {onTimeframeChange && (
              <TimeframeInput
                defaultValue={defaultTimeframe}
                hidePicker
                onChange={onTimeframeChange}
                presets={["all", "ytd", "1y", "3y", "5y"]}
              />
            )}
          </Box>
          {hasData && enableContentLocker ? (
            <Flex gap="md">
              <SortActionIcon
                active={sortDirection === "best"}
                onClick={() => setSortDirection("best")}
                type="best"
                ariaLabel={t("components.PerformanceCard.SortBest")}
              />
              <SortActionIcon
                active={sortDirection === "worst"}
                onClick={() => setSortDirection("worst")}
                type="worst"
                ariaLabel={t("components.PerformanceCard.SortWorst")}
              />
            </Flex>
          ) : null}
        </Group>
        <Skeleton visible={loading}>
          {!hasData ? (
            <HammerPriceCardEmptyState title={emptyStateTitle} variant={emptyStateVariant} />
          ) : enableContentLocker ? (
            <Box h={240}>
              <Flex align="center" justify="center" h="100%">
                <ContentLocker
                  showPlanModal={showPlanModal}
                  openPlanModal={() => openPlanModal()}
                  closePlanModal={() => closePlanModal()}
                />
              </Flex>
            </Box>
          ) : (
            <PerformanceBars
              align="left"
              data={prices}
              formatValue={(value) => formatCurrency(value, currency.code)}
              header={{
                left: t("components.HammerPriceCard.header.status"),
                right: t("components.HammerPriceCard.header.auction"),
              }}
            />
          )}
        </Skeleton>
      </Stack>
    </>
  );
};
